import React, { useState } from 'react';
import './YoutubeStoryteller.css';

const YoutubeStorytellerStep4 = ({ formData, scriptA, scriptB, titleIdeas = [], usage, onBack, onSelectScript }) => {
  const [expandedScript, setExpandedScript] = useState(null); // 'A' or 'B' or null

  const handleViewScript = (scriptId, variant) => {
    if (onSelectScript) {
      onSelectScript(scriptId, variant);
    }
  };

  const toggleExpand = (script) => {
    setExpandedScript(expandedScript === script ? null : script);
  };

  const formatScript = (script) => {
    // Split script into paragraphs and add numbering for easy reference
    const paragraphs = script.split('\n\n').filter(p => p.trim().length > 0);
    return paragraphs.map((p, idx) => (
      <div key={idx} className="yt-script-paragraph">
        <div className="yt-paragraph-number">{idx + 1}</div>
        <div className="yt-paragraph-text">{p}</div>
      </div>
    ));
  };

  const ScriptCard = ({ script, variant }) => {
    const scriptData = variant === 'A' ? scriptA : scriptB;
    const isExpanded = expandedScript === variant;

    return (
      <div className="yt-script-card">
        {/* Header */}
        <div className="yt-script-card-header">
          <div className="yt-script-variant-badge">
            Script {variant}
          </div>
          <div className="yt-script-saved-badge">
            💾 Auto-Saved
          </div>
        </div>

        {/* Title */}
        <h3 className="yt-script-title">{scriptData.title}</h3>

        {/* Stats */}
        <div className="yt-script-stats">
          <div className="yt-script-stat">
            <span className="yt-stat-icon">⏱️</span>
            <span>{scriptData.estimatedDuration}</span>
          </div>
          <div className="yt-script-stat">
            <span className="yt-stat-icon">📝</span>
            <span>{scriptData.wordCount} words</span>
          </div>
          {scriptData.reelSegments && scriptData.reelSegments.length > 0 && (
            <div className="yt-script-stat">
              <span className="yt-stat-icon">📱</span>
              <span>{scriptData.reelSegments.length} reels</span>
            </div>
          )}
        </div>

        {/* Hook Preview */}
        <div className="yt-script-hook-preview">
          <div className="yt-hook-label">🎣 Hook (First 15 seconds):</div>
          <div className="yt-hook-text">"{scriptData.hook}"</div>
        </div>

        {/* Script Preview (Collapsed) */}
        {!isExpanded && (
          <div className="yt-script-preview">
            <div className="yt-preview-text">
              {scriptData.fullScript.substring(0, 300)}...
            </div>
            <button 
              className="yt-btn-expand"
              onClick={() => toggleExpand(variant)}
            >
              Read Full Script →
            </button>
          </div>
        )}

        {/* Script Full View (Expanded) */}
        {isExpanded && (
          <div className="yt-script-full">
            <div className="yt-full-script-content">
              {formatScript(scriptData.fullScript)}
            </div>
            
            {scriptData.reelSegments && scriptData.reelSegments.length > 0 && (
              <div className="yt-reel-segments-section">
                <h4 className="yt-reel-segments-title">
                  📱 Reel-Ready Segments ({scriptData.reelSegments.length})
                </h4>
                <div className="yt-reel-segments-grid">
                  {scriptData.reelSegments.map((segment, idx) => (
                    <div key={idx} className="yt-reel-segment-card">
                      <div className="yt-reel-segment-number">#{segment.segmentNumber}</div>
                      <div className="yt-reel-hook">
                        <strong>Hook:</strong> {segment.hook}
                      </div>
                      <div className="yt-reel-body">
                        {segment.body}
                      </div>
                      <div className="yt-reel-duration">{segment.estimatedDuration}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button 
              className="yt-btn-collapse"
              onClick={() => toggleExpand(null)}
            >
              ← Collapse
            </button>
          </div>
        )}

        {/* View & Edit Button */}
        <button
          className="yt-btn-view-script"
          onClick={() => handleViewScript(scriptData.id, variant)}
        >
          🎬 Open Script {variant} →
        </button>
      </div>
    );
  };

  return (
    <div className="yt-step4-container">
      {/* Header */}
      <div className="yt-step-header">
        <h2 className="yt-step-title">
          ✅ Scripts Generated & Saved!
        </h2>
        <p className="yt-step-description">
          2 unique script variations + 20 title ideas have been auto-saved. Click on any script to view, edit, or export!
        </p>
      </div>

      {/* Scripts Comparison */}
      <div className="yt-scripts-grid">
        <ScriptCard script={scriptA} variant="A" />
        <ScriptCard script={scriptB} variant="B" />
      </div>

      {/* Title Ideas Section */}
      {titleIdeas && titleIdeas.length > 0 && (
        <div className="yt-titles-section">
          <div className="yt-titles-header">
            <h3 className="yt-titles-title">
              🎨 20 Ultra-Clickable Title Ideas
            </h3>
          </div>
          
          <div className="yt-titles-grid-compact">
            {titleIdeas.map((idea) => (
              <div 
                key={idea.id} 
                className={`yt-title-card-compact yt-title-${idea.category}`}
                onClick={() => {
                  navigator.clipboard.writeText(idea.title);
                  alert('✅ Title copied!');
                }}
              >
                <div className="yt-title-compact-header">
                  <span className="yt-title-compact-id">#{idea.id}</span>
                  <span className={`yt-title-category-badge-compact yt-category-${idea.category}`}>
                    {idea.category}
                  </span>
                </div>
                <div className="yt-title-compact-text">{idea.title}</div>
                <div className="yt-title-compact-hint">Click to copy</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="yt-step-actions">
        <button 
          className="yt-btn-secondary"
          onClick={onBack}
        >
          ← Back
        </button>
      </div>
    </div>
  );
};

export default YoutubeStorytellerStep4;

